import { useId, type ReactNode } from 'react'
import { ErrorMessage, Mtds, OwnerId } from 'prove-common'
import {
  getDelegationTotals,
  setQuantityAtPath,
  type DelegationNode
} from '../../utils/taskDelegationUtils'
import { OWNER_TYPE_LABEL } from '../../utils/delegationExplorerPath'
import { useDelegationWorksheetOrg } from './useDelegationWorksheetOrg'
import {
  buildWorksheetRows,
  parseDelegationPaste,
  type WorksheetRow
} from './delegationWorksheetUtils'
import { HIDDEN_DELEGATION_UNITS } from '../../config/hiddenDelegationUnits'
import {
  clearVisibleDelegationsAtPath,
  fillDelegationRemainder
} from '../../utils/delegationActions'

type DelegationWorksheetProps = {
  plannedQuantity: number
  delegations: DelegationNode[]
  onChange: (delegations: DelegationNode[]) => void
  canEditQuantity: (ownerId: OwnerId) => boolean
  rootPath: OwnerId[]
  rootLabel?: string
  navigation: ReactNode
  hiddenUnits?: ReadonlySet<string>
}

export const DelegationWorksheet = ({
  plannedQuantity,
  delegations,
  onChange,
  canEditQuantity,
  rootPath,
  rootLabel,
  navigation,
  hiddenUnits = HIDDEN_DELEGATION_UNITS
}: Readonly<DelegationWorksheetProps>) => {
  const org = useDelegationWorksheetOrg(rootPath, rootLabel, hiddenUnits)
  const rows = buildWorksheetRows(
    delegations,
    org.leaves,
    rootPath,
    hiddenUnits,
    canEditQuantity
  )

  const actionsPopoverId = useId()
  const editableRows = rows.filter((row) => row.active && row.canEdit)
  const { root: scopedRootDelegation, remainder } = getDelegationTotals(
    delegations,
    rootPath,
    plannedQuantity
  )
  const canClearRootRetained =
    scopedRootDelegation != null &&
    (scopedRootDelegation.retainedQuantity ?? 0) > 0 &&
    canEditQuantity(scopedRootDelegation.ownerId)
  const canClear =
    canClearRootRetained || editableRows.some((row) => row.plannedQuantity > 0)

  const setQuantity = (row: WorksheetRow, quantity: number): void => {
    if (!Number.isInteger(quantity) || quantity < 0) return
    onChange(setQuantityAtPath(delegations, row.path, quantity))
  }

  const copyDelegations = async (): Promise<void> => {
    const value = JSON.stringify({
      version: 1,
      entries: rows
        .filter((row) => row.active && !row.blockedByLegacyDelegation)
        .map((row) => ({
          ownerId: row.path.at(-1)?.toString(),
          ownerName: row.names.at(-1),
          plannedQuantity: row.plannedQuantity
        }))
    })
    try {
      await navigator.clipboard.writeText(value)
      Mtds.toast.success('Fordelingene er kopiert')
    } catch {
      Mtds.toast.danger('Kunne ikke kopiere fordelingene')
    }
  }

  const pasteDelegations = async (): Promise<void> => {
    try {
      const value = await navigator.clipboard.readText()
      const pastedRows = parseDelegationPaste(value, pasteTargets)
      if (pastedRows.length === 0) {
        Mtds.toast.danger('Utklippstavlen inneholder ingen fordelinger')
        return
      }
      const errors = pastedRows.filter((row) => row.error != null)
      if (errors.length > 0) {
        Mtds.toast.danger(
          `Kunne ikke lime inn: ${errors[0].error} på linje ${errors[0].line}`
        )
        return
      }
      const changes = pastedRows.flatMap((pastedRow) => {
        const row = rows.find(
          (candidate) => candidate.path.at(-1)?.toString() === pastedRow.ownerId
        )
        return row == null || pastedRow.quantity == null
          ? []
          : [{ row, after: pastedRow.quantity }]
      })
      onChange(
        changes.reduce(
          (next, { row, after }) => setQuantityAtPath(next, row.path, after),
          delegations
        )
      )
      Mtds.toast.success('Fordelingene er limt inn')
    } catch {
      Mtds.toast.danger('Kunne ikke lese fra utklippstavlen')
    }
  }

  const clearDelegations = (): void => {
    onChange(
      clearVisibleDelegationsAtPath(
        delegations,
        rootPath,
        hiddenUnits,
        canEditQuantity
      )
    )
  }

  const distributeRemainder = (): void => {
    onChange(
      fillDelegationRemainder(
        delegations,
        editableRows.map((row) => row.path),
        remainder
      )
    )
  }

  const pasteTargets = new Map(
    rows.map((row) => [
      row.path.at(-1)?.toString() ?? '',
      {
        key: row.key,
        minimum: 0,
        active: row.active,
        editable: row.canEdit
      }
    ])
  )

  if (org.error != null) return <ErrorMessage error={org.error} />
  if (org.isLoading) return <Mtds.Skeleton />

  return (
    <Mtds.Grid data-gap={5}>
      <Mtds.Flex
        data-justify='space-between'
        data-align='center'
        data-gap={3}
        className='max-sm:flex-col max-sm:items-start'
      >
        {navigation}
        <Mtds.Flex data-gap={2} className='flex-wrap'>
          <Mtds.Button
            data-variant='secondary'
            disabled={!canClear}
            onClick={clearDelegations}
          >
            Fjern alle
          </Mtds.Button>
          <Mtds.Button
            data-variant='secondary'
            disabled={remainder <= 0 || editableRows.length === 0}
            onClick={distributeRemainder}
          >
            Fyll resten likt
          </Mtds.Button>
          <div>
            <Mtds.Button
              data-variant='secondary'
              popoverTarget={actionsPopoverId}
            >
              Flere handlinger
            </Mtds.Button>
            <Mtds.Popover as='menu' id={actionsPopoverId}>
              <li>
                <Mtds.Button
                  type='button'
                  onClick={() => void copyDelegations()}
                >
                  Kopier fordelinger
                </Mtds.Button>
              </li>
              <li>
                <Mtds.Button
                  type='button'
                  onClick={() => void pasteDelegations()}
                >
                  Lim inn fordelinger
                </Mtds.Button>
              </li>
            </Mtds.Popover>
          </div>
        </Mtds.Flex>
      </Mtds.Flex>
      <figure>
        <Mtds.Table
          aria-label='Fordelingsliste'
          data-size='sm'
          data-mobile='stacked'
        >
          <Mtds.Table.Thead>
            <Mtds.Table.Tr>
              <Mtds.Table.Th>Enhet</Mtds.Table.Th>
              <Mtds.Table.Th data-numeric className='w-24'>
                Planlagt
              </Mtds.Table.Th>
              <Mtds.Table.Th data-numeric className='text-right'>
                Tatt
              </Mtds.Table.Th>
              <Mtds.Table.Th data-numeric className='text-right'>
                Gjenstår
              </Mtds.Table.Th>
            </Mtds.Table.Tr>
          </Mtds.Table.Thead>
          <Mtds.Table.Tbody>
            {rows.map((row) => {
              const ownerId = row.path.at(-1)!
              const remaining = Math.max(
                row.plannedQuantity - row.samplesTaken,
                0
              )
              return (
                <Mtds.Table.Tr key={row.key}>
                  <Mtds.Table.Td>
                    <Mtds.Grid data-gap={1}>
                      <Mtds.Flex data-align='center' data-gap={2}>
                        <strong>{row.names.at(-1)}</strong>
                        <Mtds.Tag data-color='neutral' data-icon='none'>
                          {OWNER_TYPE_LABEL[ownerId.type]}
                        </Mtds.Tag>
                      </Mtds.Flex>
                      <Mtds.Muted>
                        {row.names.slice(0, -1).join(' / ') || 'Rotnivå'}
                      </Mtds.Muted>
                    </Mtds.Grid>
                  </Mtds.Table.Td>
                  <Mtds.Table.Td data-numeric className='w-24'>
                    <Mtds.Field
                      as='input'
                      type='number'
                      aria-label={`Antall for ${row.names.at(-1)}`}
                      min={0}
                      step={1}
                      value={row.plannedQuantity}
                      className='w-20'
                      disabled={!row.active || !row.canEdit}
                      onChange={(event) =>
                        setQuantity(row, Number(event.target.value))
                      }
                    />
                  </Mtds.Table.Td>
                  <Mtds.Table.Td data-numeric className='align-middle'>
                    <Mtds.Flex data-align='center' data-justify='end'>
                      {row.samplesTaken}
                    </Mtds.Flex>
                  </Mtds.Table.Td>
                  <Mtds.Table.Td data-numeric className='align-middle'>
                    <Mtds.Flex data-align='center' data-justify='end'>
                      {remaining}
                    </Mtds.Flex>
                  </Mtds.Table.Td>
                </Mtds.Table.Tr>
              )
            })}
          </Mtds.Table.Tbody>
        </Mtds.Table>
      </figure>

      {rows.length === 0 && (
        <Mtds.Alert data-color='info'>
          Ingen enheter matcher filtrene.
        </Mtds.Alert>
      )}
    </Mtds.Grid>
  )
}
